import {ActionIcon, createStyles, Group, Tooltip} from '@mantine/core';
import React, {useCallback} from 'react';

import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {
  REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  REDESIGN_HEADER_ICON_CHIP_INSET_PX,
} from '../../ui/AIStyleConstants';
import type {ChatSessionStarUpdate} from '../ChatSessionMenu';
import RightSidebarToggle from '../RightSidebarToggle';
import {redesignHeaderIconChipStyles} from './RedesignHeaderIconChip';
import RedesignHeaderTitle from './RedesignHeaderTitle';
import RedesignPanelToggle from './RedesignPanelToggle';
import RedesignSidebarActions from './RedesignSidebarActions';

/** Height of the semi-expanded ("minimized") header; the top dropdown drawer sits directly below it. */
export const MINIMIZED_HEADER_HEIGHT = 54;

const useStyles = createStyles(() => ({
  headerWrap: {
    flex: 'none',
    position: 'relative',
    // Above the drawer (zIndex 5) so the top dropdown slides down from behind the header.
    zIndex: 6,
    borderBottom: '1px solid #ECEEF5',
    transition: 'background 220ms ease, border-color 220ms ease',
  },
  headerWrapMenuOpen: {
    backgroundColor: '#F4F5FF',
    borderBottomColor: 'transparent',
  },
  bar: {
    height: MINIMIZED_HEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // The left inset must match the expanded view's floating hamburger so the icon does not shift
    // between states; the right side uses the same value for symmetry.
    padding: `0 ${REDESIGN_HEADER_ICON_CHIP_INSET_PX}px`,
    position: 'relative',
  },
  menuButton: redesignHeaderIconChipStyles,
  menuButtonActive: {
    backgroundColor: REDESIGN_HEADER_ICON_CHIP_BACKGROUND,
  },
  titleCenter: {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '55%',
  },
  rightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },
}));

export interface RedesignMinimizedHeaderProps {
  activeChatId: string | null;
  /**
   * Whether to show the right-sidebar toggle. Resolved by the parent from the active chat type so the
   * toggle only appears for chats that actually mount RightSidebarLayout (see AISidepanelV2).
   */
  showRightSidebarToggle: boolean;
  /** Whether the nav/history dropdown is open (reuses the sidebar's showHistory state). */
  isMenuOpen: boolean;
  onToggleMenu: () => void;
  onExpandPanel: () => void;
  /** Start a new chat without opening the nav dropdown (mirrors the "New Chat" nav action). */
  onNewChat: () => void;
  /** On a phone the panel can't be expanded further, so the expand control becomes a close (X). */
  isPhoneWidth: boolean;
  onStarUpdated?: (update: ChatSessionStarUpdate) => Promise<void>;
}

/**
 * The compact 54px header shown in the redesigned chat sidepanel's semi-expanded ("minimized") width:
 * a hamburger that toggles the top dropdown drawer, the centered chat title, and — while the dropdown
 * is open — search/settings; otherwise the expand control. The dropdown itself is the shared drawer
 * rendered by the sidepanel, so this component is header-only.
 */
export default function RedesignMinimizedHeader({
  activeChatId,
  showRightSidebarToggle,
  isMenuOpen,
  onToggleMenu,
  onExpandPanel,
  onNewChat,
  isPhoneWidth,
  onStarUpdated,
}: RedesignMinimizedHeaderProps): JSX.Element {
  const {classes, cx} = useStyles();
  const handleClose = useCallback(() => {
    SidepanelActions.close();
  }, []);
  return (
    <div className={cx(classes.headerWrap, isMenuOpen && classes.headerWrapMenuOpen)}>
      <div className={classes.bar}>
        <Group spacing="xs">
          <ActionIcon
            aria-label="Toggle navigation"
            variant="subtle"
            size="md"
            className={cx(classes.menuButton, isMenuOpen && classes.menuButtonActive)}
            onClick={onToggleMenu}
          >
            <BenchlingIcon icon="hamburger" />
          </ActionIcon>
          <Tooltip label="Create a new chat">
            <ActionIcon
              aria-label="New chat"
              variant="subtle"
              size="md"
              className={classes.menuButton}
              onClick={onNewChat}
            >
              <BenchlingIcon icon="comment-outline" />
            </ActionIcon>
          </Tooltip>
        </Group>
        {activeChatId != null && (
          <div className={classes.titleCenter}>
            <RedesignHeaderTitle chatId={activeChatId} onStarUpdated={onStarUpdated} />
          </div>
        )}
        <div className={classes.rightGroup}>
          {showRightSidebarToggle && activeChatId != null && <RightSidebarToggle />}
          {isMenuOpen ? (
            <>
              <RedesignSidebarActions />
              {!isPhoneWidth && (
                <ActionIcon
                  aria-label="Expand"
                  variant="subtle"
                  size="md"
                  className={classes.menuButton}
                  onClick={onExpandPanel}
                >
                  <BenchlingIcon icon="chevrons-right" />
                </ActionIcon>
              )}
            </>
          ) : isPhoneWidth ? (
            <ActionIcon
              aria-label="Close"
              variant="subtle"
              size="md"
              className={classes.menuButton}
              onClick={handleClose}
            >
              <BenchlingIcon icon="xmark" />
            </ActionIcon>
          ) : (
            <RedesignPanelToggle expanded={false} />
          )}
        </div>
      </div>
    </div>
  );
}
